import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

type Status = "working" | "success" | "error";

const ConfirmEmailPage = () => {
  const [status, setStatus] = useState<Status>("working");
  const [message, setMessage] = useState("Confirming your email...");
  const [email, setEmail] = useState("");
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const run = async () => {
      const url = new URL(window.location.href);
      const hash = new URLSearchParams(url.hash.replace(/^#/, ""));
      const params = url.searchParams;

      const errorDescription = params.get("error_description") || hash.get("error_description");
      const tokenHash = params.get("token_hash") || params.get("token");
      const type = (params.get("type") || "signup") as any;
      const code = params.get("code");
      const accessToken = hash.get("access_token");
      const refreshToken = hash.get("refresh_token");

      if (errorDescription) {
        setStatus("error");
        setMessage(errorDescription);
        return;
      }

      try {
        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) throw error;
        } else if (tokenHash) {
          const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
          if (error) throw error;
        } else if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
        } else {
          const { data } = await supabase.auth.getSession();
          if (!data.session) {
            setStatus("error");
            setMessage("This confirmation link is missing its verification code. Request a new one below.");
            return;
          }
        }

        setStatus("success");
        setMessage("Your email is confirmed. You're all set!");
        setTimeout(() => navigate("/"), 2500);
      } catch (err: any) {
        setStatus("error");
        setMessage(
          err?.message?.toLowerCase?.().includes("expired")
            ? "This confirmation link has expired or was already used. Request a new one below."
            : err?.message || "We couldn't confirm your email. Request a new link below."
        );
      }
    };
    run();
  }, [navigate]);

  const resend = async () => {
    if (!email) {
      toast({ title: "Enter your email", variant: "destructive" });
      return;
    }
    setResending(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: `${window.location.origin}/auth/confirm` },
    });
    setResending(false);
    if (error) {
      toast({ title: "Couldn't resend", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Verification email sent", description: "Check your inbox (and spam folder)." });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container flex items-center justify-center py-16 md:py-24">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-[var(--card-shadow)]">
          {status === "working" && <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-primary" />}
          {status === "success" && <CheckCircle2 className="mx-auto mb-4 h-10 w-10 text-primary" />}
          {status === "error" && <XCircle className="mx-auto mb-4 h-10 w-10 text-destructive" />}
          <h1 className="font-serif text-2xl text-foreground">
            {status === "success" ? "Email confirmed" : status === "error" ? "Confirmation problem" : "Confirming..."}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{message}</p>

          {status === "error" && (
            <div className="mt-6 space-y-3 text-left">
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button onClick={resend} disabled={resending} className="w-full rounded-lg">
                {resending ? "Sending..." : "Resend verification email"}
              </Button>
            </div>
          )}

          {status === "success" && (
            <Link to="/login" className="mt-6 inline-block text-sm text-primary hover:underline">
              Continue to sign in
            </Link>
          )}
        </div>
      </main>
    </div>
  );
};

export default ConfirmEmailPage;
